import { useEffect, useState, useRef } from 'react';
import avatar from './avatar.png';

const BadgeDesignModal = ({ isOpen, onClose, ticket, onSave }) => {
  const [design, setDesign] = useState({
    badge_design: {
      name_x: 50,
      name_y: 150,
      name_size: 16,
      custom_text_x: 50,
      custom_text_y: 170,
      custom_text_size: 12,
      qr_x: 300,
      qr_y: 50,
      avatar_x: 50,
      avatar_y: 50
    }
  });
  
  const containerRef = useRef(null);
  const [activeElement, setActiveElement] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // Reset the state whenever the ticket or isOpen changes
  useEffect(() => {
    if (isOpen && ticket) {
      const defaultDesign = {
        name_x: 50,
        name_y: 150,
        name_size: 16,
        custom_text_x: 50,
        custom_text_y: 170,
        custom_text_size: 12,
        qr_x: 300,
        qr_y: 50,
        avatar_x: 50,
        avatar_y: 50
      };
      
      setDesign({
        badge_design: ticket.badge_design || defaultDesign
      });
    }
  }, [ticket, isOpen]);

  const handleFontSizeChange = (e) => {
    const { name, value } = e.target;
    setDesign(prev => ({
      badge_design: {
        ...prev.badge_design,
        [name]: parseInt(value, 10)
      }
    }));
  };

  const handleMouseDown = (e, element) => {
    if (!containerRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    setActiveElement(element);
    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);

    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !activeElement || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    
    const newX = e.clientX - containerRect.left - dragOffset.x;
    const newY = e.clientY - containerRect.top - dragOffset.y;

    // Ensure elements stay within container bounds
    const boundedX = Math.max(0, Math.min(newX, containerRect.width));
    const boundedY = Math.max(0, Math.min(newY, containerRect.height));

    setDesign(prev => ({
      badge_design: {
        ...prev.badge_design,
        [`${activeElement}_x`]: Math.round(boundedX),
        [`${activeElement}_y`]: Math.round(boundedY)
      }
    }));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setActiveElement(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleReset = () => {
    setDesign({
      badge_design: {
        name_x: 50,
        name_y: 150,
        name_size: 16,
        custom_text_x: 50,
        custom_text_y: 170,
        custom_text_size: 12,
        qr_x: 300,
        qr_y: 50,
        avatar_x: 50,
        avatar_y: 50
      }
    });
  };

  const handleSave = () => {
    console.log("Saving badge design:", design.badge_design);
    
    const updatedTicket = {
      ...ticket,
      badge_design: design.badge_design
    };
    onSave(updatedTicket);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] overflow-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Edit Badge Design</h2>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Controls - 1 column on mobile, 1/3 width on desktop */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium border-b pb-2">Design Settings</h3>

            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
              <p className="font-medium">Drag & Drop Instructions:</p>
              <p>Click and drag any element on the badge preview to position it as needed.</p>
            </div>

            {/* Font Sizes */}
            <div className="space-y-2 mt-4">
              <h4 className="font-medium text-gray-700">Font Sizes</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700">Name Font Size</label>
                  <input
                    type="number"
                    name="name_size"
                    value={design.badge_design.name_size}
                    onChange={handleFontSizeChange}
                    className="mt-1 block w-full rounded-md border p-2 text-sm border-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700">Custom Text Font Size</label>
                  <input
                    type="number"
                    name="custom_text_size"
                    value={design.badge_design.custom_text_size}
                    onChange={handleFontSizeChange}
                    className="mt-1 block w-full rounded-md border p-2 text-sm border-gray-300"
                  />
                </div>
              </div>
              
              <button
                onClick={handleReset}
                className="mt-4 px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium w-full"
              >
                Reset to Default Positions
              </button>
            </div>
          </div>

          {/* Preview Section - 1 column on mobile, 2/3 width on desktop */}
          <div className="border rounded-lg p-4 md:col-span-2 bg-gray-50 overflow-auto">
            <h3 className="text-lg font-medium border-b pb-2 mb-4">Live Preview (Drag elements to position)</h3>
            <div
              ref={containerRef}
              id="badge-container"
              className="relative inline-block border border-gray-300 rounded overflow-hidden"
              style={{ position: 'relative', display: 'inline-block', border: '1px solid black' }}
            >
              <div id="image-container">
                <img
                  src={ticket?.badge_image || "/api/placeholder/800/500"}
                  alt="Badge Template"
                  id="badge_image"
                  className="block w-full h-auto"
                />
              </div>
              
              <div
                id="badge-name-overlay"
                className={`absolute text-red-600 font-bold ${activeElement === 'name' ? 'ring-2 ring-blue-500' : ''}`}
                style={{
                  left: `${design.badge_design.name_x}px`,
                  top: `${design.badge_design.name_y}px`,
                  fontSize: `${design.badge_design.name_size}px`,
                  position: 'absolute',
                  color: 'red',
                  fontWeight: 'bold',
                  cursor: 'move'
                }}
                onMouseDown={(e) => handleMouseDown(e, 'name')}
              >
                Participant Name
              </div>
              
              <div
                id="badge-custom-text-overlay"
                className={`absolute text-red-600 ${activeElement === 'custom_text' ? 'ring-2 ring-blue-500' : ''}`}
                style={{
                  left: `${design.badge_design.custom_text_x}px`,
                  top: `${design.badge_design.custom_text_y}px`,
                  fontSize: `${design.badge_design.custom_text_size}px`,
                  position: 'absolute',
                  color: 'red',
                  cursor: 'move'
                }}
                onMouseDown={(e) => handleMouseDown(e, 'custom_text')}
              >
                {ticket?.custom_text || 'Custom Text'}
              </div>
              
              <img
                id="badge-qr-overlay"
                className={activeElement === 'qr' ? 'ring-2 ring-blue-500' : ''}
                style={{
                  left: `${design.badge_design.qr_x}px`,
                  top: `${design.badge_design.qr_y}px`,
                  position: 'absolute',
                  cursor: 'move',
                  width: '200px',
                  height: '200px'
                }}
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAADIAQMAAACXljzdAAAABlBMVEX///8AAABVwtN+AAAACXBIWXMAAA7EAAAOxAGVKw4bAAAA/ElEQVRYheWXvRGDMAyFxVG49AgehdHMaIziESgpOCv6s0lyyQKSGiM+Gp6eZBvgb1TUaAALL8ny7o8c+sMtUwpbS7fkySMp/KpODW5ezpgkU+lFHfeESx+WjF64APCAX13ihIz5lq9Fuv6Zb2HIE+k9+QgfRDXgRXxQrpW9wabwSNjkRcioczYfRCFqcppvuqPLqEdRxxmhSX4D7qD7tgiipohD7Mg2E+347JGMsM9gRdy3EyIRnXy8m1ET8OSD6RBv5BAFamPH89OqM687JAXtTE7BGizU6Nm6PhzpYJ5AUcwxmfftimcwYr0w1eleiZV+3D7g6wwbgfyNF/UxBAs59J9lAAAAAElFTkSuQmCC"
                alt="QR Code"
                onMouseDown={(e) => handleMouseDown(e, 'qr')}
              />
              
              <img
                id="badge-avatar-overlay"
                className={activeElement === 'avatar' ? 'ring-2 ring-blue-500' : ''}
                style={{
                  left: `${design.badge_design.avatar_x}px`,
                  top: `${design.badge_design.avatar_y}px`,
                  position: 'absolute',
                  cursor: 'move',
                  width: '310px',
                  height: '310px',
                  objectFit: 'cover'
                }}
                src={avatar}
                alt="Avatar"
                onMouseDown={(e) => handleMouseDown(e, 'avatar')}
              />
            </div>
            
            <p className="mt-4 text-sm text-gray-500">
              Click and drag elements to position them on the badge template.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end space-x-3">
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={handleSave}
          >
            Save Design
          </button>
        </div>
      </div>
    </div>
  );
};

export default BadgeDesignModal